import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Button, Card, Input, Select } from '@/components/common';
import { getErrorMessage } from '@/services';
import { bankStatementsService } from '@/services/bankStatements';
import {
  QUEUE_TITLE,
  reconciliationDashboardService,
  type DashQueue,
  type DashTable,
  type SignOffStep,
} from '@/services/reconciliationDashboard';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatCurrency, formatDate } from '@/utils';
import { ReconciliationTabs } from './ReconciliationTabs';

/**
 * #691: Reconciliation > Overview. One month (and optionally one bank account): cards, a table per
 * insurer / POSP / bank account, and the exception queues. A card count is a link that opens exactly the
 * rows it counts (?queue=). Bank cards are lines dated in the month; insurer figures run up to the end of
 * the month; POSP figures are as of today. Confirmed matches only.
 */

const QUEUES = Object.keys(QUEUE_TITLE) as DashQueue[];
const money = (v: string | null | undefined) => (v == null ? '-' : formatCurrency(Number(v)));
const thisMonth = () => new Date().toISOString().slice(0, 7);

function CountLink({ n, queue, params, label }: { n: number; queue: DashQueue; params: string; label: string }) {
  return (
    <Link
      to={`/reconciliation/overview?${params}&queue=${queue}`}
      aria-label={`${label}: ${n}`}
      className="font-semibold text-primary-700 hover:underline dark:text-primary-400"
    >
      {n}
    </Link>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-gray-900 dark:text-gray-100">{children}</span>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return <th className={`px-3 py-2 text-xs font-medium text-gray-500 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
}

export function ReconciliationDashboardPage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') || thisMonth();
  const account = params.get('bank_account') || '';
  const queueName = (params.get('queue') as DashQueue | null) ?? null;
  const canEdit = useCan('reconciliation', 'edit');
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [reopening, setReopening] = useState(false);

  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setParams(next);
  };
  const linkParams = `month=${month}${account ? `&bank_account=${account}` : ''}`;

  const accounts = useQuery({ queryKey: ['bankStatementAccounts'], queryFn: () => bankStatementsService.accounts() });
  const summary = useQuery({
    queryKey: ['reconDashboard', month, account],
    queryFn: () => reconciliationDashboardService.summary(month, account || undefined),
  });
  const queue = useQuery({
    queryKey: ['reconDashboardQueue', queueName, month, account],
    queryFn: () => reconciliationDashboardService.queue(queueName!, month, account || undefined),
    enabled: !!queueName,
  });

  const signOff = useMutation({
    mutationFn: (step: SignOffStep) => reconciliationDashboardService.signOff(step, month, account, reason),
    onSuccess: (_d, step) => {
      toast.success(step === 'reopen' ? 'Month reopened' : step === 'approve' ? 'Month signed off' : 'Month prepared');
      setReason('');
      setReopening(false);
      qc.invalidateQueries({ queryKey: ['reconDashboard'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const download = (name: DashTable | DashQueue) =>
    reconciliationDashboardService.exportCsv(name, month, account || undefined).catch((e) => toast.error(getErrorMessage(e)));

  const s = summary.data;
  const c = s?.cards;
  const so = s?.sign_off;

  const exportButton = (name: DashTable | DashQueue) => (
    <Button size="sm" variant="secondary" onClick={() => download(name)} aria-label={`Export ${name} as CSV`}>
      Export CSV
    </Button>
  );

  return (
    <div>
      <PageHeader
        title="Reconciliation overview"
        subtitle="Where we stand with each insurer, each POSP and the bank"
        breadcrumbs={[{ label: 'Reconciliation' }, { label: 'Overview' }]}
      />
      <ReconciliationTabs />

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <Input label="Month" name="month" type="month" value={month} onChange={(e) => e.target.value && setParam({ month: e.target.value, queue: '' })} />
        <Select
          label="Bank account"
          name="bank_account"
          value={account}
          placeholder="All bank accounts"
          options={(accounts.data ?? []).map((a) => ({ value: a.id, label: `${a.name} (${a.code})` }))}
          onChange={(e) => setParam({ bank_account: e.target.value, queue: '' })}
        />
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Bank cards count statement lines dated in the month. Insurer figures run to the end of the month. POSP figures are as of today.
        Only confirmed matches are counted.
      </p>

      {summary.isError && <p role="alert" className="text-sm text-red-600">{getErrorMessage(summary.error)}</p>}

      {c && s && (
        <>
          <div className="mb-6 grid gap-4 md:grid-cols-3">
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Bank credits</h3>
              <Stat label="Matched">
                {c.bank_credits.matched.count} / {money(c.bank_credits.matched.amount)}
              </Stat>
              <Stat label="Unmatched">
                <CountLink n={c.bank_credits.unmatched.count} queue="unmatched_credits" params={linkParams} label="Unmatched credits" /> /{' '}
                {money(c.bank_credits.unmatched.amount)}
              </Stat>
              {c.bank_credits.ignored.count > 0 && (
                <Stat label="Ignored">
                  {c.bank_credits.ignored.count} / {money(c.bank_credits.ignored.amount)}
                </Stat>
              )}
            </Card>
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Bank debits</h3>
              <Stat label="Matched">
                {c.bank_debits.matched.count} / {money(c.bank_debits.matched.amount)}
              </Stat>
              <Stat label="Unmatched">
                <CountLink n={c.bank_debits.unmatched.count} queue="unmatched_debits" params={linkParams} label="Unmatched debits" /> /{' '}
                {money(c.bank_debits.unmatched.amount)}
              </Stat>
              {c.bank_debits.ignored.count > 0 && (
                <Stat label="Ignored">
                  {c.bank_debits.ignored.count} / {money(c.bank_debits.ignored.amount)}
                </Stat>
              )}
            </Card>
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Insurer receivable</h3>
              <Stat label="Expected">{money(c.insurer_receivable.expected)}</Stat>
              <Stat label="Received">{money(c.insurer_receivable.received)}</Stat>
              <Stat label="TDS">{money(c.insurer_receivable.tds)}</Stat>
              <Stat label="Outstanding">{money(c.insurer_receivable.outstanding)}</Stat>
            </Card>
            <Card>
              <h3 className="mb-2 text-sm font-semibold">POSP payable</h3>
              <Stat label="Earned (net of clawbacks and TDS)">{money(c.posp_payable.earned)}</Stat>
              <Stat label="Paid (proved by bank or receipt)">{money(c.posp_payable.paid)}</Stat>
              <Stat label="Unpaid">{money(c.posp_payable.unpaid)}</Stat>
              <Stat label="Payouts not confirmed">
                <CountLink n={c.posp_payable.unconfirmed_payouts} queue="payouts_not_in_bank" params={linkParams} label="Payouts not in the bank" />
              </Stat>
            </Card>
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Unexplained difference</h3>
              <Stat label="Total">{money(c.unexplained.total)}</Stat>
              <Stat label="Insurer short or excess">
                <CountLink n={s.queues.short_excess} queue="short_excess" params={linkParams} label="Short or excess insurer payments" /> /{' '}
                {money(String(Number(c.unexplained.insurer_short) + Number(c.unexplained.insurer_excess)))}
              </Stat>
              <Stat label="Paid but not proved by the bank">{money(c.unexplained.posp_paid_not_proved)}</Stat>
            </Card>
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Statement closing vs book balance</h3>
              <Stat label="Statement closing">{money(c.balance.statement_closing)}</Stat>
              <Stat label="Book balance">{money(c.balance.book_balance)}</Stat>
              <Stat label="Difference">{money(c.balance.difference)}</Stat>
              {c.balance.accounts_without_statement > 0 && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  {c.balance.accounts_without_statement} bank account(s) have no statement closing for this month.
                </p>
              )}
            </Card>
          </div>

          {queueName && (
            <Card className="mb-6" >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  {QUEUE_TITLE[queueName]} ({queue.data?.rows.length ?? s.queues[queueName]})
                </h3>
                <div className="flex gap-2">
                  {exportButton(queueName)}
                  <Button size="sm" variant="secondary" onClick={() => setParam({ queue: '' })}>
                    Close
                  </Button>
                </div>
              </div>
              {queue.data && queue.data.rows.length === 0 && <p className="text-sm text-gray-500">Nothing here.</p>}
              {queue.data && queue.data.rows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" aria-label={QUEUE_TITLE[queueName]}>
                    <thead>
                      <tr>{queue.data.columns.map((col) => <Th key={col}>{col.replace(/_/g, ' ')}</Th>)}</tr>
                    </thead>
                    <tbody>
                      {queue.data.rows.map((r, i) => (
                        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                          {queue.data.columns.map((col) => <td key={col} className="px-3 py-2">{String(r[col] ?? '')}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-2 text-xs">
                <Link className="text-primary-700 hover:underline dark:text-primary-400" to={
                  queueName === 'unmatched_credits' ? '/reconciliation/insurer-receipts' : '/reconciliation/bank-statements'}>
                  Work these in {queueName === 'unmatched_credits' ? 'Insurer receipts' : 'Bank statements and POSP payouts'}
                </Link>
              </p>
            </Card>
          )}

          <Card className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Per insurer</h3>
              {exportButton('insurers')}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" aria-label="Per insurer">
                <thead>
                  <tr>
                    <Th>Insurer</Th><Th right>Expected</Th><Th right>Received</Th><Th right>TDS</Th><Th right>Outstanding</Th>
                    <Th right>On account</Th><Th>Oldest unmatched credit</Th>
                  </tr>
                </thead>
                <tbody>
                  {s.tables.insurers.map((r) => (
                    <tr key={r.insurer} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2">{r.insurer_name}</td>
                      <td className="px-3 py-2 text-right">{money(r.expected)}</td>
                      <td className="px-3 py-2 text-right">{money(r.received)}</td>
                      <td className="px-3 py-2 text-right">{money(r.tds)}</td>
                      <td className="px-3 py-2 text-right">{money(r.outstanding)}</td>
                      <td className="px-3 py-2 text-right">{money(r.on_account)}</td>
                      <td className="px-3 py-2">{r.oldest_unmatched_credit ? formatDate(r.oldest_unmatched_credit) : '-'}</td>
                    </tr>
                  ))}
                  {s.tables.insurers.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-3 text-gray-500">No insurer figures yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Per POSP</h3>
              {exportButton('posps')}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" aria-label="Per POSP">
                <thead>
                  <tr>
                    <Th>POSP</Th><Th right>Earned</Th><Th right>Paid</Th><Th right>Unpaid</Th><Th right>Marked paid, not proved</Th>
                    <Th right>Unconfirmed payouts</Th>
                  </tr>
                </thead>
                <tbody>
                  {s.tables.posps.map((r) => (
                    <tr key={r.posp} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2">{r.posp_code} {r.posp_name}</td>
                      <td className="px-3 py-2 text-right">{money(r.earned)}</td>
                      <td className="px-3 py-2 text-right">{money(r.paid)}</td>
                      <td className="px-3 py-2 text-right">{money(r.unpaid)}</td>
                      <td className="px-3 py-2 text-right">{money(r.difference)}</td>
                      <td className="px-3 py-2 text-right">{r.unconfirmed_payouts}</td>
                    </tr>
                  ))}
                  {s.tables.posps.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-3 text-gray-500">No POSP activity yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Per bank account</h3>
              {exportButton('accounts')}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" aria-label="Per bank account">
                <thead>
                  <tr><Th>Bank account</Th><Th right>Statement closing</Th><Th right>Book balance</Th><Th right>Difference</Th><Th>Month</Th></tr>
                </thead>
                <tbody>
                  {s.tables.accounts.map((r) => (
                    <tr key={r.bank_account} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2">{r.bank_account_name}</td>
                      <td className="px-3 py-2 text-right">{money(r.statement_closing)}</td>
                      <td className="px-3 py-2 text-right">{money(r.book_balance)}</td>
                      <td className="px-3 py-2 text-right">{money(r.difference)}</td>
                      <td className="px-3 py-2">{r.sign_off === 'signed_off' ? 'Signed off' : r.sign_off === 'in_progress' ? 'Prepared' : 'Open'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="mb-6">
            <h3 className="mb-2 text-sm font-semibold">Exception queues</h3>
            <ul className="space-y-1 text-sm">
              {QUEUES.map((q) => (
                <li key={q} className="flex items-center justify-between">
                  <span>{QUEUE_TITLE[q]}</span>
                  <span className="flex items-center gap-3">
                    <CountLink n={s.queues[q]} queue={q} params={linkParams} label={QUEUE_TITLE[q]} />
                    {exportButton(q)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="mb-6">
            <h3 className="mb-2 text-sm font-semibold">Month-end sign-off (optional)</h3>
            {!account && <p className="text-sm text-gray-500">Choose a bank account to sign off its month.</p>}
            {account && so && (
              <div className="space-y-2 text-sm">
                <p>
                  Status: <strong>{so.status === 'signed_off' ? 'Signed off, matching is locked' : so.status === 'in_progress' ? 'Prepared' : 'Open'}</strong>
                  {so.prepared_by && <> . Prepared by {so.prepared_by}</>}
                  {so.approved_by && <> . Approved by {so.approved_by}</>}
                </p>
                {canEdit && so.status !== 'signed_off' && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => signOff.mutate('prepare')}>Prepare for sign-off</Button>
                    {so.status === 'in_progress' && <Button size="sm" onClick={() => signOff.mutate('approve')}>Approve and lock</Button>}
                  </div>
                )}
                {canEdit && so.status === 'signed_off' && !reopening && (
                  <Button size="sm" variant="secondary" onClick={() => setReopening(true)}>Reopen</Button>
                )}
                {reopening && (
                  <div className="flex items-end gap-2">
                    <Input label="Why is this month being reopened?" name="reopen_reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <Button size="sm" disabled={!reason.trim()} onClick={() => signOff.mutate('reopen')}>Confirm reopen</Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
